/* eslint-disable react/jsx-no-bind */
import React from 'react'
import T from 'prop-types'
import IT from 'react-immutable-proptypes'
import cx from 'classnames'
import {
  DimensionAware,
  DragSelectionContainer
} from 'react-base'
import {
  scaleLinear,
  scaleTime,
  scaleUtc,
  scaleSequential
} from 'd3-scale'
import {
  interpolateSpectral
} from 'd3-scale-chromatic'
import memoize from 'memoize-one'
import {
  Map,
  List
} from 'immutable'
import {
  COLORSET,
  GRAPH_AXES,
  GRAPH_STROKE_STYLES,
  CURVE_OPTIONS
} from '../constants'
import Axis, {AXIS_SIDE_OPTIONS} from './Axis'
import GraphKeyEntry from './GraphKeyEntry'
import { SCHEMA_SHAPE } from '../../shapes'
import TimelineImpl2D from './2d/TimelineImpl2D'
import TimelineImpl3D from './3d/TimelineImpl3D'

const Z_INDEX_OFFSET = -10 // World uni distance between each stream stack
const ORTHO_CAMERA_INITIAL = false

const verticalAxisWidth = 60
const horizontalAxisHeight = 40
const sanitizeDataKey = dataKey => dataKey.replace(/\./g, '_')


class Timeline extends React.PureComponent {
  static displayName = "Schemer.Timeline"

  static propTypes = {
    className: T.string,

    axisTop: T.bool,
    axisRight: T.bool,
    axisBottom: T.bool,
    axisLeft: T.bool,

    xPad: T.number,
    yPad: T.number,

    fillColor: T.string,
    fillOpacity: T.number,
    strokeColor: T.string,
    strokeOpacity: T.number,
    strokeWidth: T.number,

    onItemMouseOver: T.func,
    onItemMouseOut: T.func,
    onItemClick: T.func,

    xExponent: T.number, // If set, x scale will be a nonlinear pow() function with this exponent

    // Optional domain/scale overrides
    overrideMinX: T.number,
    overrideMaxX: T.number,
    overrideMinY: T.number,
    overrideMaxY: T.number,
    overrideXScale: T.func, // Parent override for X scale
    overrideYScale: T.func, // Parent override for Y scale
    overrideMinColor: T.number,
    overrideMaxColor: T.number,
    overrideColorScale: T.func,
    schema: SCHEMA_SHAPE.isRequired,
    data: IT.listOf(
      IT.mapOf(T.number, T.string)
    ),

    pathInterpolateInitial: T.oneOf(Object.keys(CURVE_OPTIONS)),
    keyVisibleInitial: T.bool,

    stackStreams: T.bool, // If true, this will be a stacked area graph. If false, each dataKey will be rendered from the same zero baseline
    is3D: T.bool,

    innerAxes: T.bool, // If true, axes will be overlaid "inside" the graph bounds, with appropriate padding and transparency

    overlayRenderer: T.func, // optional function called to render any overlay elements. Passed (data, xScale, yScale)
    useUtc: T.bool,

    // From DimensionAware
    height: T.number,
    width: T.number,

    transitionDuration: T.number,
    transitionType: T.string, // 'play' ...

    minYForceZeroInitial: T.bool,
    allowZoomXInitial: T.bool,
    allowZoomYInitial: T.bool,

    debug: T.bool,
  }

  static defaultProps = {
    height: 100,
    width: 100,
    axisTop: false,
    axisRight: false,
    axisBottom: false,
    axisLeft: false,
    strokeWidth: 2,
    strokeOpacity: 1.0,
    fillOpacity: 0.75,
    stackStreams: false,
    innerAxes: false,
    keyVisibleInitial: false,
    useUtc: false,
    is3D: false,
    transitionDuration: 1000,
    transitionType: 'none',
    debug: false,
    minYForceZeroInitial: false,
    xPad: 10,
    yPad: 10,
    allowZoomXInitial: false,
    allowZoomYInitial: false,
  }

  constructor(props) {
    super(props)
    this.state = {
      maximized: false,

      perspective: !ORTHO_CAMERA_INITIAL,

      enableCameraSpin: true,
      cameraDistance: 110, //ORTHO_CAMERA_INITIAL ? 0 : 40,
      cameraElevation: -20,//ORTHO_CAMERA_INITIAL ? 0 : 50,
      cameraAngle: 4.69, //ORTHO_CAMERA_INITIAL ? 0 : 50,
      cameraLookAt: {x:0, y:0, z:0},

      pathInterpolate: props.pathInterpolateInitial || CURVE_OPTIONS.MONOTONE, //MONOTONE,

      orthoCamera: true,
      keyVisible: props.keyVisibleInitial,

      selectedXFieldKey: null, // Single time (presumably) axis field
      selectedYFieldKeys: List(), // Collection of fields to render in the Y-Axis
      selectedColorFieldKey: null, // Single color axis field

      snapPoint: null,
      snapPointX: null,
      snapPointY: null,

      minYForceZero: props.minYForceZeroInitial,

      overrideMinX: null,
      overrideMaxX: null,
      overrideMinY: null,
      overrideMaxY: null,

      allowZoomX: props.allowZoomXInitial,
      allowZoomY: props.allowZoomYInitial
    }
  }

  toggleKeyVisible = () => {
    this.setState({
      keyVisible: !this.state.keyVisible
    })
  }

  // getAxisDropdownSchema = () => {
  //   // FIXME make this only export fields for axes that have available options

  //   return new Schema({
  //     // yAxis: {},
  //     // colorAxis: {},
  //     xAxis: {
  //       display: 'axis',
  //       type: TYPES.ENUM,
  //       required: false,
  //       // options: [
  //       //   {display: "Category One", id: "c_One"},
  //       //   {display: "Category Two", id: "c_Two"},
  //       //   {display: "Category Three", id: "c_Three"}
  //       // ],
  //       options: {
  //         c_One: "Category One",
  //         c_Two: "Category Two",
  //         c_Three: "Category Three"
  //       },
  //       editable: true
  //     }
  //   })
  // }

  // handleSelectedXFieldChange = newXField => {
  //   console.log('handler selectedXFieldKey', newXField)

  //   this.setState({
  //     selectedXFieldKey: newXField
  //   })
  // }

  handleDragSelection = (selection, shiftHeld, e, xScale, yScale) => {
    const {
      allowZoomX,
      allowZoomY
    } = this.state
    const {
      startX,
      startY,
      endX,
      endY,
      boundary
    } = selection

    const newState = {}

    // Support R->L, L->R, U->D, and D->U rect draw orders.
    // Clamp to previous x/y extents.
    if (allowZoomX) {
      const startXRelative = xScale.invert(startX - boundary.left)
      const endXRelative = xScale.invert(endX - boundary.left)
      const minX = Math.min(startXRelative, endXRelative)
      const maxX = Math.max(startXRelative, endXRelative)
      const xDomain = xScale.domain()

      // Sanity checking for drags that end outside of the previous scale bounds
      // if (maxX - minX > 1) {}
      newState.overrideMinX = Math.max(xDomain[0], Math.min(minX, maxX))
      newState.overrideMaxX = Math.min(xDomain[1], Math.max(minX, maxX))
    }
    if (allowZoomY) {
      const yDomain = yScale.domain()
      const startYRelative = yScale.invert(startY - boundary.top)
      const endYRelative = yScale.invert(endY - boundary.top)
      const minY = Math.min(startYRelative, endYRelative)
      const maxY = Math.max(startYRelative, endYRelative)
      newState.overrideMinY = Math.max(yDomain[0], minY)
      newState.overrideMaxY = Math.min(yDomain[1], maxY)
    }

    this.setState(newState)
  }

  handleZoomReset = () => {
    this.setState({
      overrideMinX: null,
      overrideMaxX: null,
      overrideMinY: null,
      overrideMaxY: null
    })
  }

  handleKeyMouseOver = dataKey => {
    this.setState({
      highlightedPathKey: dataKey
    })
  }

  handleKeyMouseOut = () => {
    this.setState({
      highlightedPathKey: null
    })
  }

  handlePathMouseOver = e => {
    const {
      offsetX,
      offsetY,
      target
    } = e
    const {
      xScale,
      yScale,
      values,
      dataKey,
      strokeColor,
      fillColor
    } = target

    const mouseTime = xScale.invert(offsetX)
    const valueKeys = Object.keys(values)

    let snapPointTimestamp = valueKeys[0]
    for (let i = 0, iLen = valueKeys.length; i < iLen; i++) {
      const tickTimestamp = valueKeys[i]
      const nextTickTimestamp = valueKeys[i + 1]
      if (!nextTickTimestamp) {
        snapPointTimestamp = tickTimestamp
        break
      }
      if (tickTimestamp <= mouseTime && nextTickTimestamp > mouseTime) {
        const split = (nextTickTimestamp - tickTimestamp) / 2
        snapPointTimestamp = mouseTime > (tickTimestamp + split) ? nextTickTimestamp : tickTimestamp
        break
      }
    }
    const snapPoint = values[`${snapPointTimestamp}`]

    this.setState({
      highlightedPathKey: dataKey,
      mouseX: Math.round(offsetX),
      mouseY: Math.round(offsetY),
      snapPointValue: snapPoint && snapPoint.y1Value,
      snapPointColor: strokeColor || fillColor,
      snapPointX: snapPoint && Math.round(xScale(snapPointTimestamp)),
      snapPointY: snapPoint && Math.round(yScale(snapPoint.y1))
    })
  }

  handlePathMouseOut = () => {
    this.setState({
      highlightedPathKey: null,
      mouseX: null,
      mouseY: null,
      snapPoint: null,
      snapPointX: null,
      snapPointY: null
    })
  }

  getFields = memoize((schema, selectedXFieldKey, selectedYFieldKeys, selectedColorFieldKey) => {
    if (!schema) {
      return
    }
    const graphableFields = schema.getGraphable()
    const fieldsByAxis = graphableFields.groupBy(field => field.getIn(['graph', 'axis']))

    const xFields = fieldsByAxis.get(GRAPH_AXES.X)
    const xFieldKeys = xFields ? xFields.keySeq().toList() : List()
    const yFields = fieldsByAxis.get(GRAPH_AXES.Y)
    const yFieldKeys = yFields ? yFields.keySeq().toList() : List()
    const colorFields = fieldsByAxis.get(GRAPH_AXES.COLOR)
    const colorFieldKeys = colorFields ? colorFields.keySeq().toList() : List()


    const yFieldKeysToStyles = yFields.map((field, fieldKey) => {
      const defaultColor = COLORSET[(yFieldKeys.indexOf(fieldKey) * 2) % COLORSET.length]

      // Return schema-defined timelineStyles, merged with generated defaults
      const defaultStyles = Map({
        fillColor: defaultColor,
        strokeColor: defaultColor,
        strokeStyle: GRAPH_STROKE_STYLES.SOLID,
        strokeWidth: 1
      })

      return defaultStyles.merge(field.getIn(['graph', 'timelineStyle']))
    })

    return {
      xFields,
      xFieldKeys,
      yFields,
      yFieldKeys,
      yFieldKeysToStyles,
      colorFields,
      colorFieldKeys,

      activeXFieldKey: selectedXFieldKey || xFieldKeys.first(),
      activeYFieldKeys: selectedYFieldKeys && selectedYFieldKeys.size > 0 ? selectedYFieldKeys : yFieldKeys, // Y supports multiple via stack OR parallel
      activeColorFieldKey: selectedColorFieldKey || colorFieldKeys.first()
    }
  })

  getData = memoize((
    height,
    width,
    data,
    schema,
    axisTop,
    axisRight,
    axisBottom,
    axisLeft,
    overrideMinX,
    overrideMaxX,
    overrideMinY,
    overrideMaxY,
    overrideMinColor,
    overrideMaxColor,
    overrideXScale,
    overrideYScale,
    overrideColorScale,
    useUtc,
    stackStreams,
    is3D,
    activeXFieldKey,
    activeYFieldKeys,
    activeColorFieldKey,
    minYForceZero,
    xPad,
    yPad
  ) => {
    if (!data || !height || !width) {
      return {}
    }

    const d3TimeScale = useUtc ? scaleUtc : scaleTime //  getD3TimeScale()

    const sortedData = data.sortBy(d => d.get(activeXFieldKey))
    let minX = sortedData.first().get(activeXFieldKey)
    let maxX = sortedData.last().get(activeXFieldKey)
    let minY = minYForceZero ? 0 : null
    let maxY = 0 // Calculate maximum y value
    let minColor = 0
    let maxColor = 0
    const _tickTotals = {}
    const renderData = activeYFieldKeys.map((fieldKey, i) => {
      const values = sortedData.reduce((out, d) => {
        const _val = d.get(fieldKey, 0)
        const _time = +d.get(activeXFieldKey)
        const _colorVal = +d.get(activeColorFieldKey)

        let y0 = 0
        let y1 = _val

        if (stackStreams) {
          _tickTotals[_time] = _tickTotals[_time] || 0 //y0
          y0 = _tickTotals[_time]
          y1 = _tickTotals[_time] += _val
        }

        if (!minYForceZero) {
          minY = minY === null ? y1 : Math.min(minY, y1)
        }

        maxY = Math.max(maxY, y1)

        minColor = Math.min(minColor, _colorVal)
        maxColor = Math.max(maxColor, _colorVal)

        out[`${_time}`] = {
          timestamp: _time,
          colorValue: _colorVal,
          y1Value: _val,
          y0,
          y1,
        }
        return out
      }, {})

      return {
        z: i * Z_INDEX_OFFSET,
        dataKey: fieldKey,
        name: sanitizeDataKey(fieldKey),
        values
      }
    }) //.toArray() //.reverse()

    minX = overrideMinX || minX
    maxX = overrideMaxX || maxX
    minY = overrideMinY || minY
    maxY = overrideMaxY || maxY

    const xScale = overrideXScale || d3TimeScale()
      .domain([
        minX,
        maxX
      ])
      .range([
        (axisLeft ? verticalAxisWidth : 0) + xPad,
        width - (axisRight ? verticalAxisWidth : 0) - xPad
      ])

    const yScale = overrideYScale || scaleLinear()
      .domain([
        minY,
        maxY
      ])
      .range([
        height - (axisBottom ? horizontalAxisHeight : 0) - yPad,
        (axisTop ? horizontalAxisHeight : 0) + yPad
      ])
      .nice()

    const colorInterpolator = interpolateSpectral
    // const colorInterpolator = interpolateRdBu
    // const colorInterpolator = interpolatePlasma
    // const colorScale = activeColorFieldKey && (overrideColorScale || scaleSequential(colorInterpolator)
    //   .domain([
    //     overrideMaxColor || maxColor, // 0=Hot
    //     overrideMinColor || minColor // 1=Cold
    //   ]))
    const colorScale = overrideColorScale || scaleSequential(colorInterpolator)
      .domain([
        overrideMaxColor || maxColor, // 0=Hot
        overrideMinColor || minColor // 1=Cold
      ])

    return {
      xScale,
      yScale,
      colorScale,
      minX,
      maxX,
      minY,
      maxY,
      renderData
    }
  })

  render() {
    const {
      className,
      height,
      width,
      axisTop,
      axisRight,
      axisBottom,
      axisLeft,
      xExponent,
      // fillColor,
      // fillOpacity,
      // strokeColor,
      // strokeWidth,
      // strokeOpacity,
      innerAxes,
      overlayRenderer,
      data,
      schema,
      stackStreams,
      is3D,
      xPad,
      yPad,
      // overrideMinX,
      // overrideMaxX,
      // overrideMinY,
      // overrideMaxY,
      overrideMinColor,
      overrideMaxColor,
      overrideXScale,
      overrideYScale,
      overrideColorScale,
      useUtc,
      debug
    } = this.props
    const {
      pathInterpolate,
      keyVisible,
      maximized,
      cameraDistance,
      cameraAngle,
      cameraElevation,
      selectedXFieldKey,
      selectedYFieldKeys,
      selectedColorFieldKey,

      snapPointColor,
      snapPointX,
      snapPointY,

      highlightedPathKey,

      minYForceZero,

      overrideMinX,
      overrideMaxX,
      overrideMinY,
      overrideMaxY,

      allowZoomX,
      allowZoomY
    } = this.state

    if (!data || !schema || !height || !width) {
      return <div className="schemer_timeline no_data" />
    }

    const {
      xFields,
      xFieldKeys,
      yFields,
      yFieldKeys,
      yFieldKeysToStyles,
      colorFields,
      colorFieldKeys,
      activeXFieldKey,
      activeYFieldKeys,
      activeColorFieldKey,
    } = this.getFields(schema, selectedXFieldKey, selectedYFieldKeys, selectedColorFieldKey)

    const {
      xScale,
      yScale,
      colorScale,
      minX,
      maxX,
      minY,
      maxY,
      renderData
    } = this.getData(
      height,
      width,
      data,
      schema,
      axisTop,
      axisRight,
      axisBottom,
      axisLeft,
      overrideMinX,
      overrideMaxX,
      overrideMinY,
      overrideMaxY,
      overrideMinColor,
      overrideMaxColor,
      overrideXScale,
      overrideYScale,
      overrideColorScale,
      useUtc,
      stackStreams,
      is3D,
      activeXFieldKey,
      activeYFieldKeys,
      activeColorFieldKey,
      minYForceZero,
      xPad,
      yPad
    )

    const isZoomed = overrideMinX || overrideMaxX || overrideMinY || overrideMaxY

    // All of the activeFields used in the Y Axis should be the same data type, whether stacked or not.
    // The axis will use the first entry's schema for Axis/label formatting
    const yAxisFieldKey = activeYFieldKeys.first()

    const use3DContext = false // FIXME finish 3D and make it a config option

    const TimelineImpl = use3DContext ? TimelineImpl3D : TimelineImpl2D

    return (
      <DragSelectionContainer
        tagName="div"
        className={cx(
          `schemer_timeline`,
          innerAxes ? 'inner_axes' : 'outer_axes',
          className,
          {
            timeline_maximized: maximized,
            can_zoom: allowZoomX || allowZoomY,
            no_zoom_x: !allowZoomX,
            no_zoom_y: !allowZoomY
          }
        )}
        onSelection={this.handleDragSelection}
        onSelectionArgs={[xScale, yScale]}
        coordinateSelection
      >
        {debug &&
          <div style={{zIndex: 9999, position: 'absolute'}}>
            <button onClick={() => this.setState({cameraElevation: cameraElevation + 10})}>ELEV +</button>
            <button onClick={() => this.setState({cameraElevation: cameraElevation - 10})}>ELEV -</button>
            <button onClick={() => this.setState({cameraDistance: cameraDistance + 10})}>DIST +</button>
            <button onClick={() => this.setState({cameraDistance: cameraDistance - 10})}>DIST -</button>
            <button onClick={() => this.setState({cameraAngle: cameraAngle + .10})}>ANGL +</button>
            <button onClick={() => this.setState({cameraAngle: cameraAngle - .10})}>ANGL -</button>
          </div>
        }
        <TimelineImpl
          {...{
            height,
            width,

            xScale,
            yScale,
            colorScale,
            minX,
            maxX,
            minY,
            maxY,
            renderData,

            xFields,
            xFieldKeys,
            yFields,
            yFieldKeys,
            yFieldKeysToStyles,
            colorFields,
            colorFieldKeys,
            activeXFieldKey,
            activeYFieldKeys,
            activeColorFieldKey,

            selectedXFieldKey,
            selectedYFieldKeys,
            selectedColorFieldKey,

            minYForceZero,

            pathInterpolate,
            xExponent,

            highlightedPathKey,
            onPathMouseMove: this.handlePathMouseOver,
            onPathMouseOver: this.handlePathMouseOver,
            onPathMouseOut: this.handlePathMouseOut
          }}
        />
        { minY < 0 && maxY > 0 &&
          <div
            className="zero_line"
            style={{
              top: Math.floor(yScale(0))
            }}
          />
        }
        {axisTop && xScale &&
          <Axis
            side={AXIS_SIDE_OPTIONS.TOP}
            scale={xScale}
            width={width}
            height={horizontalAxisHeight}
            field={schema.get(activeXFieldKey)}
            fieldKey={activeXFieldKey}
            mousePos={snapPointX}
          />
        }
        {axisBottom && xScale &&
          <Axis
            side={AXIS_SIDE_OPTIONS.BOTTOM}
            scale={xScale}
            width={width}
            height={horizontalAxisHeight}
            field={schema.get(activeXFieldKey)}
            fieldKey={activeXFieldKey}
            mousePos={snapPointX}
          />
        }
        {axisLeft && yScale &&
          <Axis
            side={AXIS_SIDE_OPTIONS.LEFT}
            scale={yScale}
            width={verticalAxisWidth}
            height={height}
            field={schema.get(yAxisFieldKey)}
            fieldKey={yAxisFieldKey}
            mousePos={snapPointY}
          />
        }
        {axisRight && yScale &&
          <Axis
            side={AXIS_SIDE_OPTIONS.RIGHT}
            scale={yScale}
            width={verticalAxisWidth}
            height={height}
            field={schema.get(yAxisFieldKey)}
            fieldKey={yAxisFieldKey}
            mousePos={snapPointY}
          />
        }
        <div
          className={`timeline_key ${keyVisible ? 'key_visible' : ''}`}
          style={{
            top: yScale.range()[1],
            left: xScale.range()[0]
          }}
        >
          <span
            className="btn-icon icon-compass2 key_btn"
            data-tooltip={`${keyVisible ? 'Hide' : 'Show'} Graph Key`}
            onClick={this.toggleKeyVisible}
          />
          {keyVisible &&
            <div className="graph_key_entries">
              {yFieldKeysToStyles.entrySeq().map(([dataKey, styles]) => {
                return (
                  <GraphKeyEntry
                    key={dataKey}
                    dataKey={dataKey}
                    styles={styles}
                    schema={schema}
                    highlightedPathKey={highlightedPathKey}
                    onMouseOver={this.handleKeyMouseOver}
                    onMouseMove={this.handleKeyMouseOver}
                    onMouseOut={this.handleKeyMouseOut}
                  />
                )
              })}
              {colorFieldKeys}
            </div>
          }
        </div>
        {/* <div className="timeline_config">

          <Input
            fieldName={'xAxis'}
            schema={axisSchema}
            field={axisSchema.get('xAxis')}
            label={'Selected X/Time Field'}
            onValidationChange={console.log}
            onChange={this.handleSelectedXFieldChange}
            currentValue={activeXFieldKey}
          />
        </div> */}
        {snapPointX && snapPointY &&
          <div
            className="snap_point_overlay"
          >
            {(axisTop || axisBottom) &&
              <div
                className={`snap_point_crosshair vert`}
                style={{
                  transform: `translateX(${snapPointX}px)`,
                  top: axisTop ? 0 : snapPointY,
                  bottom: axisBottom ? 0 : (height - snapPointY)
                }}
              />
            }
            {(axisLeft || axisRight) &&
              <div
                className={`snap_point_crosshair horiz`}
                style={{
                  transform: `translateY(${snapPointY}px)`,
                  left: axisLeft ? 0 : snapPointX,
                  right: axisRight ? 0 : (width - snapPointX)
                }}
              />
            }
            <div
              className="snap_point_dot"
              style={{
                background: snapPointColor,
                transform: `translate(${snapPointX}px, ${snapPointY}px)`
              }}
            />
          </div>
        }
        {isZoomed &&
          <div
            className={`zoomed_overlay`}
            style={{
              top: yScale.range()[1],
              left: xScale.range()[1]
            }}
          >
            <span
              className={`btn btn-icon icon-zoomout`}
              data-tooltip={`Reset Zoom`}
              onClick={this.handleZoomReset}
            />
          </div>
        }
        {overlayRenderer &&
          <div className="timeline_overlay">
            {overlayRenderer(data, xScale, yScale)}
          </div>
        }
      </DragSelectionContainer>
    )
  }
}

export default DimensionAware(Timeline)
